'use client';

import {
  useMemo,
  useState,
  useContext,
  useEffect,
  type FC,
  Fragment,
  useCallback,
} from 'react';
import { EngagementLetter } from './components/EngagementLetter';
import { PreviewQuestionItem } from './components/PreviewQuestionItem';
import { Flex, Details } from '@/_components';
import { Question, QuestionNote } from '@/_types/models/questionnaire';
import { Section } from '@/_types/models/sections';
import { PreviewNotesDialog } from './components/PreviewNotesDialog';
import { DeleteQuestionConfirm } from './components/DeleteQuestionConfirm';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { answerQuestion, deleteQuestion } from '@/_api/client-questionnaires';
import {
  PREDEFINED_MARKED_COMPLETED,
  PREDEFINED_NOT_APPLICABLE,
} from '@/_constants/questions';
import type { SectionsPreviewTabProps } from './SectionsPreviewTab.types';
import {
  isQuestionAnswered,
  isQuestionTriggered,
  getTriggeredQuestions,
  mapQuestionsToLevels,
  getSectionQuestions,
  getParentQuestion,
} from '@/_utils/questions';
import type { QuestionWithLevels } from '@/_types/questions';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { RelevantQuestionnaireStatus } from './components/RelevantQuestionnaireStatus';
import { CustomSection } from '@/_components/QuestionnaireContent/components/CustomSection';
import { PillProps } from '@/_components/Pill/Pill.types';
import { sectionTriggered } from '@/_utils/sections';
import { RequestSignatureDialog } from './components/RequestSignatureDialog';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';

export const SectionsPreviewTab: FC<SectionsPreviewTabProps> = ({
  questionnaire,
  highlightedQuestionIds,
  reloadQuestionnairePreview,
  isLoadingQuestionnairePreview,
}) => {
  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false);
  const [isDeleteLoadingId, setDeleteLoadingId] = useState<null | number>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState<boolean>(false);
  const [isRequestingSignature, setIsRequestingSignature] =
    useState<boolean>(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<
    number | undefined
  >(undefined);
  const [triggeredEngagementSection, setTriggeredEngagementSection] = useState<
    Section | undefined
  >(undefined);

  const { preferences } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { setQuestionnairePreview } = useContext(QuestionnaireContext);

  const scrollToElement = useCallback((): boolean => {
    if (typeof highlightedQuestionIds?.[0] !== 'number') return false;

    const element = document.getElementById(
      `question-${highlightedQuestionIds?.[0]}`,
    );

    if (!element) return false;

    const rect = element.getBoundingClientRect();
    if (!rect?.height) return false;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    return true;
  }, [highlightedQuestionIds]);

  useEffect(() => {
    if (typeof highlightedQuestionIds?.[0] !== 'number') return;

    const tryScrolling = (attempt = 0) => {
      const success = scrollToElement();

      if (success) return;
      if (attempt < 5) {
        setTimeout(() => tryScrolling(attempt + 1), 100 * (attempt + 1));
      }
    };

    tryScrolling();
  }, [scrollToElement, highlightedQuestionIds]);

  const updateQuestionAnswer = useCallback(
    (questionId: number, answer: string) => {
      setQuestionnairePreview((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sections: prev.sections?.map((section) => ({
            ...section,
            questionnaire_questions: section.questionnaire_questions?.map(
              (q) => (q.id === questionId ? { ...q, answer } : q),
            ),
          })),
          questionnaire_questions: prev.questionnaire_questions?.map((q) =>
            q.id === questionId ? { ...q, answer } : q,
          ),
        };
      });
    },
    [setQuestionnairePreview],
  );

  const onDelete = (question: Question) => {
    setIsDeletingQuestion(true);
    setSelectedQuestionId(question.id);
  };

  const onMarkCompleted = async (question: Question) => {
    try {
      updateQuestionAnswer(question.id, PREDEFINED_MARKED_COMPLETED);

      const { error } = await answerQuestion({
        answer: PREDEFINED_MARKED_COMPLETED,
        questionId: question.id,
        companyId: questionnaire.company_id!,
        token,
      });

      if (error) {
        addToast('error', 'Failed to mark question as completed');
        updateQuestionAnswer(question.id, '');
      }
    } catch (error) {
      console.error(error);
      updateQuestionAnswer(question.id, '');
    }
  };

  const onUndo = async (question: Question) => {
    try {
      updateQuestionAnswer(question.id, '');

      const { error } = await answerQuestion({
        companyId: questionnaire.company_id!,
        answer: '',
        answerBy: null,
        questionId: question.id,
        token,
      });

      if (error) {
        addToast('error', 'Failed to undo marked completed');
        updateQuestionAnswer(question.id, PREDEFINED_MARKED_COMPLETED);
      }
    } catch (error) {
      addToast('error', null, { error });
      updateQuestionAnswer(question.id, PREDEFINED_MARKED_COMPLETED);
    }
  };

  const onNotesToggle = (question?: Question) => {
    const newSelectedQuestionId: number | undefined = isNotesOpen
      ? undefined
      : question?.id;

    setSelectedQuestionId(newSelectedQuestionId);
    setIsNotesOpen(!isNotesOpen);
  };

  const onCancelRemove = () => {
    setIsDeletingQuestion(false);
    setSelectedQuestionId(undefined);
  };

  const onRemoveQuestion = async () => {
    try {
      setDeleteLoadingId(selectedQuestionId!);

      await deleteQuestion({
        questionId: selectedQuestionId!,
        companyId: questionnaire.company_id!,
        questionnaireId: questionnaire.id!,
        token,
      });

      await reloadQuestionnairePreview();

      addToast('success', 'Question deleted successfully');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setDeleteLoadingId(null);
      setIsDeletingQuestion(false);
      setSelectedQuestionId(undefined);
    }
  };

  const selectedNotes: Array<QuestionNote> = useMemo(
    () =>
      questionnaire?.sections
        ?.flatMap?.((x) => x?.questionnaire_questions)
        .find((q) => q?.id === selectedQuestionId)
        ?.filtered_questionnaire_comments || [],
    [selectedQuestionId, questionnaire],
  );

  const previewableSections: Array<Section> = useMemo(
    () =>
      questionnaire?.sections?.filter((section) =>
        sectionTriggered(
          section,
          questionnaire?.sections,
          questionnaire?.questionnaire_questions || [],
        ),
      ) || [],
    [questionnaire?.sections, questionnaire?.questionnaire_questions],
  );

  const onRequestSignature = (section: Section) => {
    setTriggeredEngagementSection(section);
    setIsRequestingSignature(true);
  };
  return (
    <>
      <DeleteQuestionConfirm
        isLoading={!!isDeleteLoadingId}
        isOpen={isDeletingQuestion}
        onClose={onCancelRemove}
        onConfirm={onRemoveQuestion}
      />

      <RequestSignatureDialog
        isOpen={isRequestingSignature}
        onClose={() => setIsRequestingSignature(false)}
        section={triggeredEngagementSection || undefined}
        companyId={questionnaire.company_id!}
        questionnaireId={questionnaire.id!}
        reloadQuestionnairePreview={reloadQuestionnairePreview}
      />

      <PreviewNotesDialog
        isOpen={isNotesOpen}
        onClose={onNotesToggle}
        companyId={questionnaire.company_id!}
        questionId={selectedQuestionId!}
        notes={selectedNotes}
        reloadQuestionnairePreview={reloadQuestionnairePreview}
      />

      <Flex isColumn={true} gap={8}>
        {/* Questionnaire Completion Status */}
        {preferences?.parent_entity_relevant_questionnaire_statuses_enabled && (
          <RelevantQuestionnaireStatus
            questionnaire={questionnaire}
            reloadQuestionnairePreview={reloadQuestionnairePreview}
          />
        )}
        {previewableSections?.map((section, index) => {
          const totalQuestions: Array<Question> = (
            section?.questionnaire_questions || []
          ).filter((q) => {
            const parentQuestion: Question | undefined = getParentQuestion(
              section,
              q,
            );
            const hasEntriesParentQuestion: boolean =
              parentQuestion?.question_type === 'entries';

            return hasEntriesParentQuestion
              ? true
              : isQuestionTriggered(section, q);
          });

          const sectionQuestions: Array<Question> = getSectionQuestions(
            section?.questionnaire_questions || [],
            section,
          );

          const questionLevels: Array<QuestionWithLevels> =
            mapQuestionsToLevels(totalQuestions);

          const finishedQuestions: Array<Question> =
            sectionQuestions?.filter((q) => isQuestionAnswered(q)) || [];

          const noteCount: number =
            section.questionnaire_questions?.reduce(
              (acc, question) =>
                acc + (question?.filtered_questionnaire_comments?.length ?? 0),
              0,
            ) ?? 0;

          const completionCount = sectionQuestions?.length
            ? `(${finishedQuestions?.length}/${sectionQuestions?.length})`
            : '';

          const isAllQuestionsAnswered: boolean =
            sectionQuestions?.length > 0 &&
            finishedQuestions?.length === sectionQuestions?.length;

          const isCompletedWithPartialQuestions: boolean =
            section?.section_type === 3 &&
            (section.all_questions_answered || section.marked_completed) &&
            !isAllQuestionsAnswered;

          const isNonQuestionnaireTypeCompleted: boolean =
            section?.section_type !== 3 && section?.marked_completed;

          const getCompletionLabel = (): string => {
            if (isAllQuestionsAnswered || isNonQuestionnaireTypeCompleted) {
              return `Completed ${completionCount}`;
            }

            if (isCompletedWithPartialQuestions) {
              return `Section marked completed ${completionCount}`;
            }

            return `Incomplete ${completionCount}`;
          };

          const getCompletionIntent = (): PillProps['intent'] => {
            if (isAllQuestionsAnswered || isNonQuestionnaireTypeCompleted) {
              return 'success';
            }

            if (isCompletedWithPartialQuestions) {
              return 'warning';
            }

            return 'danger';
          };

          const pills: Array<PillProps> = [
            noteCount && { label: `${noteCount} notes`, intent: 'secondary' },
            {
              label: getCompletionLabel(),
              intent: getCompletionIntent(),
            },
          ].filter(Boolean) as Array<PillProps>;

          const hasHighlightedQuestion: boolean = totalQuestions.some((q) =>
            highlightedQuestionIds.includes(q.id),
          );

          return (
            <Details
              key={index}
              isAlwaysClosed={section?.section_type === 4}
              isOpen={hasHighlightedQuestion}
              isShort={true}
              title={section.name}
              pills={
                isLoadingQuestionnairePreview
                  ? [{ label: 'Loading', intent: 'default', isLoading: true }]
                  : pills
              }
            >
              <Flex isColumn={true}>
                {questionLevels?.map((question1, levelKey) => {
                  const {
                    hasTriggeredQuestions: hasSecondLevel,
                    questions: secondLevelQuestions,
                  } = getTriggeredQuestions(question1, true);

                  // Question Level 1
                  return (
                    <Fragment key={levelKey}>
                      <PreviewQuestionItem
                        questionLevel={1}
                        question={question1}
                        questionnaire={questionnaire!}
                        isDeleteLoading={isDeleteLoadingId === question1.id}
                        isMarkLoading={false}
                        isHighlighted={highlightedQuestionIds.includes(
                          question1.id,
                        )}
                        onDelete={() => onDelete(question1)}
                        onMarkCompleted={onMarkCompleted}
                        onMarkUndo={onUndo}
                        onNotesToggle={onNotesToggle}
                      />
                      {hasSecondLevel &&
                        question1.answer !== PREDEFINED_NOT_APPLICABLE &&
                        secondLevelQuestions?.map(
                          (question2, firstLevelKey) => {
                            const {
                              hasTriggeredQuestions: hasThirdLevel,
                              questions: thirdLevelQuestions,
                            } = getTriggeredQuestions(question2, true);

                            // Question Level 2
                            return (
                              <Fragment key={firstLevelKey}>
                                <PreviewQuestionItem
                                  questionLevel={2}
                                  question={question2}
                                  questionnaire={questionnaire!}
                                  isDeleteLoading={
                                    isDeleteLoadingId === question2.id
                                  }
                                  isMarkLoading={false}
                                  isHighlighted={highlightedQuestionIds.includes(
                                    question2.id,
                                  )}
                                  onDelete={() => onDelete(question2)}
                                  onMarkCompleted={onMarkCompleted}
                                  onMarkUndo={onUndo}
                                  onNotesToggle={onNotesToggle}
                                />
                                {hasThirdLevel &&
                                  question2.answer !==
                                    PREDEFINED_NOT_APPLICABLE &&
                                  thirdLevelQuestions?.map(
                                    (question3, thirdLevelKey) => {
                                      // Question Level 3
                                      return (
                                        <PreviewQuestionItem
                                          key={thirdLevelKey}
                                          questionLevel={3}
                                          questionnaire={questionnaire!}
                                          question={question3}
                                          isDeleteLoading={
                                            isDeleteLoadingId === question3.id
                                          }
                                          isMarkLoading={false}
                                          isHighlighted={highlightedQuestionIds.includes(
                                            question3.id,
                                          )}
                                          onDelete={() => onDelete(question3)}
                                          onMarkCompleted={onMarkCompleted}
                                          onMarkUndo={onUndo}
                                          onNotesToggle={onNotesToggle}
                                        />
                                      );
                                    },
                                  )}
                              </Fragment>
                            );
                          },
                        )}
                    </Fragment>
                  );
                })}
                {section.section_type === 0 && (
                  <CustomSection section={section} showInstruction={false} />
                )}
                {totalQuestions?.length < 1 && section.section_type === 2 && (
                  <EngagementLetter
                    section={section}
                    onRequestSignature={onRequestSignature}
                    questionnaire={questionnaire}
                    reloadQuestionnairePreview={reloadQuestionnairePreview}
                  />
                )}
              </Flex>
            </Details>
          );
        })}
      </Flex>
    </>
  );
};
